<template>
  <div class="admin-container">
    <h1>{{ isEdit ? '编辑产品' : '添加产品' }}</h1>
    <form class="product-form" @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="title">产品标题（中文） *</label>
        <input
          type="text"
          id="title"
          v-model="formData.title"
          required
          class="form-control"
        />
      </div>

      <div class="form-group">
        <label for="titleEn">产品标题（英文）</label>
        <input
          type="text"
          id="titleEn"
          v-model="formData.titleEn"
          class="form-control"
          placeholder="Product Title (English)"
        />
      </div>

      <!-- 产品描述 -->
      <div class="form-group">
        <div class="description-header">
          <label for="description">产品描述（中文）</label>
        </div>
        <textarea
          id="description"
          v-model="formData.description"
          class="form-control"
          rows="4"
        ></textarea>
      </div>

      <div class="form-group">
        <div class="description-header">
          <label for="descriptionEn">产品描述（英文）</label>
        </div>
        <textarea
          id="descriptionEn"
          v-model="formData.descriptionEn"
          class="form-control"
          rows="4"
          placeholder="Product Description (English)"
        ></textarea>
      </div>

      <!-- 改进技术参数输入方式 -->
      <div class="form-group">
        <label>技术参数</label>
        <div class="tech-specs-container">
          <div v-for="(spec, index) in techSpecs" :key="index" class="tech-spec-row">
            <input
              type="text"
              v-model="spec.key"
              placeholder="参数名称（中文，如：材质）"
              class="form-control tech-spec-key"
            />
            <input
              type="text"
              v-model="spec.value"
              placeholder="参数值（中文，如：不锈钢）"
              class="form-control tech-spec-value"
            />
            <input
              type="text"
              v-model="spec.keyEn"
              placeholder="参数名称（英文，如：Material）"
              class="form-control tech-spec-key-en"
            />
            <input
              type="text"
              v-model="spec.valueEn"
              placeholder="参数值（英文，如：Stainless Steel）"
              class="form-control tech-spec-value-en"
            />
            <button
              type="button"
              @click="removeSpec(index)"
              class="btn-remove-spec"
            >
              - 删除
            </button>
          </div>
          <button
            type="button"
            @click="addSpec()"
            class="btn-add-spec"
          >
            + 添加参数
          </button>
          <!-- 添加Excel导入功能 -->
          <div class="excel-import-section">
            <input type="file" accept=".xlsx,.xls" @change="handleExcelImport">
            <div v-if="excelImportError" class="error-message">{{ excelImportError }}</div>
            <div class="import-tip">
              <p><strong>Excel格式要求（四列，按顺序）：</strong></p>
              <p><strong>第1列：</strong>参数名称（中文，如：材质）</p>
              <p><strong>第2列：</strong>参数值（中文，如：不锈钢）</p>
              <p><strong>第3列：</strong>参数名称（英文，如：Material）</p>
              <p><strong>第4列：</strong>参数值（英文，如：Stainless Steel）</p>
              <p style="margin-top: 8px; color: #666;">支持带表头（会自动识别并跳过）或不带表头</p>
              <p style="color: #666;">空行会自动跳过</p>
            </div>
          </div>
        </div>
        <div v-if="techSpecs.length === 0" class="empty-tip">
          点击"添加参数"按钮开始添加技术参数，或通过Excel导入
        </div>
      </div>

      <!-- 新增尺寸图上传 -->
      <div class="form-group">
        <label>尺寸图</label>
        <input type="file" accept="image/*" name="dimensionImage" @change="handleDimensionImageUpload">
        <div v-if="dimensionImagePreview" class="image-preview">
          <img :src="dimensionImagePreview" alt="尺寸图预览">
        </div>
      </div>

      <!-- 产品图片上传 -->
      <!-- // 在第86-97行的产品图片上传部分进行以下修改 -->
      <!-- 产品图片上传 -->
      <div class="form-group">
        <label>产品图片（可上传多张）</label>
        <input type="file" multiple accept="image/*" @change="handleImagesUpload">
        <div class="image-upload-tips">
          <span>支持jpg、jpeg、png格式，最多上传9张图片</span>
        </div>
        <div v-if="imagePreviews.length > 0" class="image-previews">
          <div v-for="(preview, index) in imagePreviews" :key="index" class="image-preview-item">
            <img :src="preview" alt="Preview" class="preview-image">
            <button @click="removeImage(index)" class="remove-image-btn">删除</button>
          </div>
        </div>
      </div>

      <!-- 产品分类选择 -->
      <div class="form-group">
        <label for="model">产品分类 *</label>
        <select id="model" v-model="formData.model" class="form-control" required>
          <option value="">请选择分类</option>
          <option value="智能通道系统">智能通道系统</option>
          <option value="物联网中间件">物联网中间件</option>
          <option value="传感器及组件">传感器及组件</option>
        </select>
      </div>

      <!-- 产品标签选择 -->
      <div class="form-group">
        <label for="tag">产品标签</label>
        <select id="tag" v-model="formData.tag" class="form-control">
          <option value="">请选择标签</option>
          <option value="首页不展示">首页不展示</option>
          <option value="首页展示">首页展示</option>
        </select>
      </div>

      <!-- 产品排序序号 -->
      <div class="form-group">
        <label for="sortOrder">排序序号</label>
        <input
          type="number"
          id="sortOrder"
          v-model.number="formData.sortOrder"
          class="form-control"
          min="0"
        />
      </div>

      <!-- 产品状态选择 -->
      <div class="form-group">
        <label>
          <input
            type="checkbox"
            v-model="formData.isActive"
          />
          启用产品
        </label>
      </div>

      <div class="form-actions">
        <button type="button" @click="$router.go(-1)" class="btn-cancel">取消</button>
        <button type="submit" class="btn-submit">保存</button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { addProduct, getProduct, updateProduct } from '@/api/product';
// 导入xlsx库用于解析Excel文件
import * as XLSX from 'xlsx';

const router = useRouter();
const route = useRoute();
const isEdit = !!route.params.id;

// 产品表单数据
const formData = ref({
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  enableDescription: true,
  model: '',  // 产品分类
  tag: '',
  sortOrder: 0,
  isActive: true,
  imageUrl: '',  // 保留用于编辑现有产品
  imageUrls: [], // 添加这个字段存储多张图片的URL
  dimensionImageUrl: ''
});

// 技术参数相关
const techSpecs = ref([]);
const excelImportError = ref('');

// 图片上传相关变量 - 多图上传实现
const imageFiles = ref([]);
const imagePreviews = ref([]);
const dimensionImageFile = ref(null);
const dimensionImagePreview = ref('');

// 添加技术参数
const addSpec = () => {
  techSpecs.value.push({ key: '', value: '', keyEn: '', valueEn: '' });
};

// 删除技术参数
const removeSpec = (index) => {
  techSpecs.value.splice(index, 1);
};

// 处理Excel导入
const handleExcelImport = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  
  excelImportError.value = '';
  
  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      
      // 获取第一个工作表
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // 将工作表转换为JSON（不使用表头，直接获取所有行）
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      // 检查数据格式
      if (!jsonData || jsonData.length === 0) {
        excelImportError.value = 'Excel文件中没有数据';
        return;
      }
      
      // 检查第一行是否是表头（如果第一行包含"参数"、"参数值"、"参数英文"、"英文参数值"等关键词，则跳过）
      let startRow = 0;
      if (jsonData.length > 0) {
        const firstRow = jsonData[0].map(cell => String(cell || '').toLowerCase());
        const hasHeaderKeywords = firstRow.some(cell => 
          cell.includes('参数') || cell.includes('parameter') || 
          cell.includes('值') || cell.includes('value') ||
          cell.includes('英文') || cell.includes('english')
        );
        if (hasHeaderKeywords) {
          startRow = 1; // 跳过表头
        }
      }
      
      // 解析数据到techSpecs数组（四列格式：参数名称(中文)、参数值(中文)、参数名称(英文)、参数值(英文)）
      const importedSpecs = [];
      let emptyRowCount = 0;
      
      for (let i = startRow; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) {
          emptyRowCount++;
          continue;
        }
        
        // 检查是否至少第一列有数据
        const key = String(row[0] || '').trim();
        if (!key) {
          emptyRowCount++;
          continue; // 跳过空行
        }
        
        // 获取四列数据
        const value = String(row[1] || '').trim();
        const keyEn = String(row[2] || '').trim();
        const valueEn = String(row[3] || '').trim();
        
        importedSpecs.push({
          key: key,
          value: value,
          keyEn: keyEn,
          valueEn: valueEn
        });
      }
      
      if (importedSpecs.length === 0) {
        excelImportError.value = `未找到有效的技术参数数据。已跳过${emptyRowCount}个空行。请检查Excel格式：第一列应为参数名称(中文)，第二列应为参数值(中文)，第三列应为参数名称(英文)，第四列应为参数值(英文)`;
        return;
      }
      
      // 清空现有参数并添加导入的参数
      techSpecs.value = importedSpecs;
      alert(`成功导入${importedSpecs.length}条技术参数（跳过${emptyRowCount}个空行）`);
    } catch (error) {
      excelImportError.value = 'Excel文件解析失败，请检查文件格式';
    }
  };
  reader.readAsArrayBuffer(file);
};

// 在第162-173行的handleImagesUpload函数进行修改
// 处理多图片上传预览
const handleImagesUpload = (e) => {
  const files = e.target.files;
  if (files) {
    // 添加图片数量限制
    if (imageFiles.value.length + files.length > 9) {
      alert('最多只能上传9张图片');
      return;
    }
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      // 添加文件类型验证
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        alert(`文件${file.name}不是支持的图片格式`);
        continue;
      }
      
      imageFiles.value.push(file);
      imagePreviews.value.push(URL.createObjectURL(file));
    }
  }
  // 清空input值，允许重复选择相同文件
  e.target.value = '';
};

// 移除预览图片
const removeImage = (index) => {
  imageFiles.value.splice(index, 1);
  const url = imagePreviews.value.splice(index, 1)[0];
  // 释放URL对象以避免内存泄漏
  URL.revokeObjectURL(url);
};

// 处理尺寸图上传预览
const handleDimensionImageUpload = (e) => {
  const file = e.target.files[0];
  if (file) {
    dimensionImageFile.value = file;
    dimensionImagePreview.value = URL.createObjectURL(file);
  }
};

// 加载产品数据（编辑模式）
onMounted(async () => {
  if (isEdit) {
    try {
      const response = await getProduct(route.params.id);
      formData.value = {
        title: response.data.title,
        titleEn: response.data.titleEn || '',
        description: response.data.description || '',
        descriptionEn: response.data.descriptionEn || '',
        enableDescription: response.data.enableDescription !== undefined ? response.data.enableDescription : true,
        model: response.data.model || '',  // 产品分类
        tag: response.data.tag,
        sortOrder: response.data.sortOrder,
        isActive: response.data.isActive,
        imageUrl: response.data.imageUrl,
        imageUrls: response.data.imageUrls || [], // 关键点：添加这一行来保存现有图片URL
        dimensionImageUrl: response.data.dimensionImageUrl
      };
      
      techSpecs.value = [];
      
      if (response.data.techSpecsI18n && typeof response.data.techSpecsI18n === 'object') {
        const zhSpecs = response.data.techSpecsI18n.zh || {};
        const enSpecs = response.data.techSpecsI18n.en || {};
        const mapping = response.data.techSpecsI18n.mapping || {};
        
        // 使用映射表来正确匹配中英文参数
        const zhKeys = Object.keys(zhSpecs);
        
        if (zhKeys.length > 0) {
          techSpecs.value = zhKeys.map(zhKey => {
            // 通过映射表找到对应的英文键名
            const enKey = mapping[zhKey] || '';
            // 如果映射表中没有，尝试直接使用中文键名查找（兼容旧数据）
            const finalEnKey = enKey || (Object.prototype.hasOwnProperty.call(enSpecs, zhKey) ? zhKey : '');
            return {
              key: zhKey,
              value: String(zhSpecs[zhKey] || ''),
              keyEn: finalEnKey,
              valueEn: String(finalEnKey ? (enSpecs[finalEnKey] || '') : '')
            };
          });
          
          // 添加只有英文没有中文的参数（如果有）
          const usedEnKeys = new Set(Object.values(mapping));
          Object.keys(enSpecs).forEach(enKey => {
            if (!usedEnKeys.has(enKey) && !Object.prototype.hasOwnProperty.call(mapping, enKey)) {
              techSpecs.value.push({
                key: '',
                value: '',
                keyEn: enKey,
                valueEn: String(enSpecs[enKey] || '')
              });
            }
          });
        }
      }
      
      // 如果 techSpecsI18n 为空或没有数据，尝试使用 techSpecs（兼容旧数据）
      if (techSpecs.value.length === 0 && response.data.techSpecs && typeof response.data.techSpecs === 'object') {
        const specs = response.data.techSpecs;
        const specEntries = Object.entries(specs);
        if (specEntries.length > 0) {
          techSpecs.value = specEntries.map(([key, value]) => ({
            key,
            value: String(value),
            keyEn: '',
            valueEn: ''
          }));
        }
      }
      
      
      // 显示现有图片预览
      if (response.data.imageUrls && response.data.imageUrls.length > 0) {
        imagePreviews.value = response.data.imageUrls;
      }
      
      // 显示现有尺寸图预览
      dimensionImagePreview.value = response.data.dimensionImageUrl;
    } catch (error) {
      alert('加载产品数据失败，请重试');
    }
  }
});

// 提交表单
const handleSubmit = async () => {
  try {
    // 验证数据
    if (!formData.value.title.trim()) {
      alert('请输入产品标题');
      return;
    }
    
    // 验证产品分类
    if (!formData.value.model || !formData.value.model.trim()) {
      alert('请选择产品分类');
      return;
    }
    
    // 创建FormData对象处理文件上传
    const submitData = new FormData();
    submitData.append('title', formData.value.title);
    submitData.append('titleEn', formData.value.titleEn || '');
    submitData.append('description', formData.value.description || '');
    submitData.append('descriptionEn', formData.value.descriptionEn || '');
    submitData.append('enableDescription', formData.value.enableDescription);
    submitData.append('model', formData.value.model.trim());
    submitData.append('tag', formData.value.tag);
    submitData.append('sortOrder', formData.value.sortOrder);
    submitData.append('isActive', formData.value.isActive);
    
    // 添加多张图片
    imageFiles.value.forEach((file) => {
      submitData.append('images', file);
    });
    
    // 添加现有图片URL（编辑模式下）
    if (isEdit) {
      // 当没有上传新图片时，使用原有的imageUrl
      if (imageFiles.value.length === 0 && formData.value.imageUrl) {
        submitData.append('imageUrl', formData.value.imageUrl);
      }
      // 总是提交现有的imageUrls，确保服务器能够正确处理
      if (formData.value.imageUrls && formData.value.imageUrls.length > 0) {
        submitData.append('existingImageUrls', JSON.stringify(formData.value.imageUrls));
      }
    }
    
    // 添加尺寸图
    if (dimensionImageFile.value) {
      submitData.append('dimensionImage', dimensionImageFile.value);
    } else if (isEdit && formData.value.dimensionImageUrl) {
      submitData.append('dimensionImageUrl', formData.value.dimensionImageUrl);
    }

    // 转换键值对数组为JSON字符串（中文技术参数）
    // 使用中文键名作为主键，确保中英文参数能正确关联
    const specsObject = techSpecs.value.reduce((obj, spec) => {
      const key = spec.key.trim();
      if (key) {
        obj[key] = spec.value.trim();
      }
      return obj;
    }, {});
    submitData.append('techSpecs', JSON.stringify(specsObject));

    // 转换英文技术参数（使用英文键名作为键）
    const specsObjectEn = techSpecs.value.reduce((obj, spec) => {
      const keyEn = spec.keyEn.trim();
      const valueEn = spec.valueEn.trim();
      if (keyEn) {
        obj[keyEn] = valueEn;
      }
      return obj;
    }, {});
    
    // 创建键名映射表：{中文键名: 英文键名}
    const keyMapping = techSpecs.value.reduce((obj, spec) => {
      const zhKey = spec.key.trim();
      const enKey = spec.keyEn.trim();
      if (zhKey && enKey) {
        obj[zhKey] = enKey;
      }
      return obj;
    }, {});
    
    // 创建 i18n 格式的技术参数
    // zh: {中文键名: 中文值}
    // en: {英文键名: 英文值}
    // mapping: {中文键名: 英文键名} - 用于匹配中英文参数
    const techSpecsI18n = {
      zh: specsObject,
      en: specsObjectEn,
      mapping: keyMapping
    };
    submitData.append('techSpecsI18n', JSON.stringify(techSpecsI18n));

    // 添加请求头配置
    const requestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    if (isEdit) {
      await updateProduct(route.params.id, submitData, requestConfig);
      alert('产品更新成功');
      
      // 在保存成功后，更新本地的imageUrls数据
      const updatedProduct = await getProduct(route.params.id);
      if (updatedProduct.data.imageUrls && updatedProduct.data.imageUrls.length > 0) {
        formData.value.imageUrls = updatedProduct.data.imageUrls;
        imagePreviews.value = updatedProduct.data.imageUrls;
      }
    } else {
      await addProduct(submitData, requestConfig);
      alert('产品添加成功');
    }
    router.push('/admin/products');
  } catch (error) {
    alert(error.response?.data?.error || '保存产品失败，请重试');
  }
};
</script>

<style scoped lang="scss">
@import '@/styles/sections/admin/productlist/product_form';

/* Excel导入样式 */
.excel-import-section {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #ddd;
}

.excel-import-section input[type="file"] {
  margin-bottom: 10px;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 5px;
}

.import-tip {
  font-size: 12px;
  color: #666;
  margin-top: 10px;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
}

.import-tip p {
  margin: 2px 0;
}

/* 多图片预览样式 */
.image-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.image-preview-item {
  position: relative;
  width: 120px;
  height: 120px;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.remove-image-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(255, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-image-btn:hover {
  background-color: rgba(255, 0, 0, 0.9);
}

/* 技术参数中英文输入框样式 */
.tech-spec-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
}

.tech-spec-key,
.tech-spec-value,
.tech-spec-key-en,
.tech-spec-value-en {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.tech-spec-key {
  background-color: #f9f9f9;
}

.tech-spec-key-en {
  background-color: #f0f8ff;
}

.tech-spec-value {
  background-color: #f9f9f9;
}

.tech-spec-value-en {
  background-color: #f0f8ff;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .tech-spec-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .tech-spec-key,
  .tech-spec-value,
  .tech-spec-key-en,
  .tech-spec-value-en {
    width: 100%;
  }
}
</style>